import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, CreditCard, Shield, Lock, Check, Smartphone } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";

export default function EventPayment() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [eventData, setEventData] = useState(null);
  const [ticketSelection, setTicketSelection] = useState(null);
  const [guests, setGuests] = useState([]);

  const [paymentMethod, setPaymentMethod] = useState("momo");
  const [paymentData, setPaymentData] = useState({
    cardHolderName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    momoProvider: "",
    momoPhone: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Load booking data from sessionStorage
  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to continue");
      navigate("/signin");
      return;
    }

    const savedBookingData = sessionStorage.getItem(`eventBooking_${slug}`);
    if (!savedBookingData) {
      toast.error("Booking information not found");
      navigate(`/event-details/${slug}/book`);
      return;
    }

    try {
      const parsed = JSON.parse(savedBookingData);
      if (!parsed.eventData || !parsed.ticketSelection) {
        toast.error("Invalid booking data");
        navigate(`/event-details/${slug}/book`);
        return;
      }

      setEventData(parsed.eventData);
      setTicketSelection(parsed.ticketSelection);
      setGuests(parsed.guests || []);
    } catch (error) {
      console.error("Error loading booking data:", error);
      toast.error("Failed to load booking data");
      navigate(`/event-details/${slug}/book`);
    }
  }, [isAuthenticated, navigate, slug]);

  const handlePayment = async (dataToUse = null) => {
    const data = dataToUse || paymentData;

    setIsProcessing(true);
    try {
      // Always pass generic momo fields so API works; no mobile money form shown
      const isMomo = paymentMethod === "momo";
      const payload = {
        event_slug: slug,
        ticket_name: ticketSelection.ticketType.name,
        quantity: ticketSelection.quantity,
        payment_method: isMomo ? "mobile_money" : "card",
        guests: guests.map((g) => ({
          name: g.name,
          email: g.email,
          phone: g.phone,
        })),
        momo_provider: isMomo ? "mtn" : "card",
        momo_phone: "0000000000",
      };

      const response = await conferenceAndTradeMissionService.createEventBooking(payload);

      if (response.status && (response.data?.status_code === 200 || response.data?.data)) {
        const outer = response.data?.data ?? response.data;
        // Payment URL from API: data.data.api_pay_redirect_url (CalPay gateway)
        const paymentLink =
          outer?.data?.api_pay_redirect_url ??
          outer?.payment_url ??
          outer?.payment_link ??
          outer?.link ??
          outer?.redirect_url ??
          outer?.checkout_url;

        if (paymentLink) {
          toast.success("Redirecting to payment...");
          window.location.href = paymentLink;
          return;
        }

        toast.success(response.message || "Booking created successfully!");
        const innerData = outer?.data ?? outer;
        const bookingId =
          innerData?.payment_code ??
          innerData?.short_pay_code ??
          innerData?.booking_id ??
          innerData?.registration_id ??
          `#${Math.floor(Math.random() * 10000)}EVT`;
        const successData = {
          bookingId,
          eventData,
          ticketSelection,
          guests,
          paymentVerified: false, // Will be verified after gateway callback
        };
        sessionStorage.setItem(`eventBookingSuccess_${slug}`, JSON.stringify(successData));
        sessionStorage.removeItem(`eventBooking_${slug}`);
        navigate(`/event-details/${slug}/event-success`);
      } else {
        toast.error(response.message || "Payment initiation failed. Please try again.");
        setIsProcessing(false);
      }
    } catch (error) {
      toast.error(error?.message || "Payment initiation failed. Please try again.");
      setIsProcessing(false);
    }
  };

  if (!eventData || !ticketSelection) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-emerald-500/[0.08] via-teal-500/[0.06] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
              <CreditCard size={20} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="mb-0.5 flex items-center justify-start">
                <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  Payment
                </span>
              </div>
              <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
                Complete Payment
              </h1>
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm truncate">
                {eventData?.event_title}
              </p>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-700 sm:flex-none"
            >
              <ChevronLeft size={20} strokeWidth={2} />
              Back
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Step Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 shadow-md"
          >
            <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg">
              <span className="text-lg font-bold text-white">{guests.length > 0 ? "3" : "2"}</span>
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-900">
                Step {guests.length > 0 ? "3" : "2"} of {guests.length > 0 ? "3" : "2"}
              </p>
              <p className="text-xs text-emerald-700">Complete payment</p>
            </div>
          </motion.div>

          {/* Payment Details Section - Two Columns */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-6">
            {/* Left: Payment Form */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b-2 border-gray-100">
                  <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                    <Smartphone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Payment Method</h3>
                    <p className="text-sm text-gray-600">Choose how you'd like to pay</p>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <motion.button
                      type="button"
                      onClick={() => setPaymentMethod("momo")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative overflow-hidden flex flex-col gap-2 items-center p-5 rounded-xl border-2 transition-all ${
                        paymentMethod === "momo"
                          ? "bg-gradient-to-br from-emerald-50 to-teal-100/50 border-emerald-500 shadow-lg ring-2 ring-emerald-200"
                          : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md"
                      }`}
                    >
                      {paymentMethod === "momo" && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 bg-emerald-500 rounded-full"
                        >
                          <Check size={14} className="text-white" />
                        </motion.div>
                      )}
                      <div className={`p-3 rounded-xl ${paymentMethod === "momo" ? "bg-emerald-500" : "bg-gray-100"}`}>
                        <Smartphone size={24} className={paymentMethod === "momo" ? "text-white" : "text-gray-600"} />
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-bold ${paymentMethod === "momo" ? "text-emerald-900" : "text-gray-700"}`}>
                          Mobile Money
                        </div>
                        <div className="text-xs text-gray-500">Ghana (MTN, Vodafone, AirtelTigo)</div>
                      </div>
                    </motion.button>

                    <motion.button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative overflow-hidden flex flex-col gap-2 items-center p-5 rounded-xl border-2 transition-all ${
                        paymentMethod === "card"
                          ? "bg-gradient-to-br from-emerald-50 to-teal-100/50 border-emerald-500 shadow-lg ring-2 ring-emerald-200"
                          : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md"
                      }`}
                    >
                      {paymentMethod === "card" && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 bg-emerald-500 rounded-full"
                        >
                          <Check size={14} className="text-white" />
                        </motion.div>
                      )}
                      <div className={`p-3 rounded-xl ${paymentMethod === "card" ? "bg-emerald-500" : "bg-gray-100"}`}>
                        <CreditCard size={24} className={paymentMethod === "card" ? "text-white" : "text-gray-600"} />
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-bold ${paymentMethod === "card" ? "text-emerald-900" : "text-gray-700"}`}>
                          Card Payment
                        </div>
                        <div className="text-xs text-gray-500">Debit/Credit</div>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Continue to payment - no MoMo form; generic momo fields sent in background */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6"
                >
                  {isProcessing ? (
                    <div className="flex flex-col gap-4 justify-center items-center p-12 text-center bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-500" />
                        <div className="flex absolute inset-0 justify-center items-center">
                          <div className="w-8 h-8 bg-emerald-500 rounded-full animate-pulse" />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1 text-lg font-bold text-gray-900">Initiating payment</p>
                        <p className="text-sm text-gray-600">You may be redirected to complete payment...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-6">
                      <p className="mb-4 text-sm text-gray-600">
                        {paymentMethod === "card"
                          ? "You will enter your card details securely on the payment provider's page after you continue."
                          : "You will complete Mobile Money payment on the next screen."}
                      </p>
                      <motion.button
                        type="button"
                        onClick={() => {
                          if (isProcessing) return;
                          handlePayment(paymentData);
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="w-full rounded-xl bg-emerald-500 px-4 py-3 text-base font-semibold text-white shadow-md transition-colors hover:bg-emerald-600"
                      >
                        Continue to payment
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-32 p-6 bg-gradient-to-br from-emerald-50 via-white rounded-2xl border-2 border-emerald-200 shadow-xl to-emerald-50/30"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b-2 border-emerald-200">
                  <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                    <Shield className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Order Summary</h3>
                </div>
                
                <div className="mb-6 space-y-4">
                  <div className="p-4 bg-white rounded-xl border border-emerald-100">
                    <p className="mb-1 text-xs font-bold tracking-wide text-gray-500 uppercase">Event</p>
                    <p className="text-sm font-bold leading-tight text-gray-900">
                      {eventData?.event_title}
                    </p>
                  </div>

                  <div className="p-4 space-y-3 bg-white rounded-xl border border-emerald-100">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Ticket Type</span>
                      <span className="text-sm font-bold text-gray-900">{ticketSelection.ticketType.name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Quantity</span>
                      <span className="text-sm font-bold text-gray-900">{ticketSelection.quantity}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Price per ticket</span>
                      <span className="text-sm font-bold text-gray-900">
                        {ticketSelection.ticketType.price === 0 || parseFloat(ticketSelection.ticketType.price) === 0
                          ? "FREE"
                          : `GH₵ ${ticketSelection.ticketType.price}`}
                      </span>
                    </div>
                    {guests.length > 0 && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="mb-2 text-xs font-bold text-gray-500 uppercase">Guests</p>
                        <p className="text-sm text-gray-700">{guests.length} additional guest{guests.length > 1 ? "s" : ""}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-5 mb-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold tracking-wide text-emerald-700 uppercase">Total Amount</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {ticketSelection.totalAmount === 0 ? "FREE" : `GH₵ ${ticketSelection.totalAmount}`}
                    </span>
                  </div>
                </div>

                <div className="p-4 space-y-3 bg-white rounded-xl border border-emerald-100">
                  <div className="flex gap-3 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-green-100 rounded-lg">
                      <Shield size={16} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Secure Payment</p>
                      <p className="text-xs text-gray-600">Secure payment gateway</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                      <Lock size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Data Protection</p>
                      <p className="text-xs text-gray-600">Your information is safe with us</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

